/**
 * Compartir el recap: Web Share nativa cuando existe, portapapeles cuando no.
 *
 * Vive fuera del componente y recibe el `navigator` como parámetro para poder probar de
 * verdad los cuatro caminos. Con `navigator` global no se puede: el navegador donde
 * corren los tests tiene el soporte que tiene, y la rama que NO tenga se quedaría sin
 * cubrir para siempre — exactamente el tipo de verde flojo que ya nos ha mordido.
 *
 * `navigator.share` solo funciona dentro de un gesto del usuario y en contexto seguro
 * (https o localhost). Por eso la llamada cuelga directamente del click y no de un
 * `useEffect` ni de una promesa encadenada.
 */

export type ResultadoCompartir =
  /** Compartido con la hoja nativa del sistema. */
  | 'compartido'
  /** El usuario cerró la hoja nativa sin elegir destino. No es un error. */
  | 'cancelado'
  /** Sin Web Share: copiado al portapapeles. */
  | 'copiado'
  /** Ni una cosa ni la otra: hay que copiar a mano desde la vista previa. */
  | 'sin-soporte';

/** Lo mínimo que se necesita del navigator, para poder falsearlo en los tests. */
export interface NavegadorCompartir {
  share?: (datos: { text: string; title?: string }) => Promise<void>;
  clipboard?: { writeText?: (texto: string) => Promise<void> };
}

export function tieneWebShare(nav: NavegadorCompartir): boolean {
  return typeof nav.share === 'function';
}

export async function compartirTexto(
  texto: string,
  nav: NavegadorCompartir,
  titulo?: string,
): Promise<ResultadoCompartir> {
  if (typeof nav.share === 'function') {
    try {
      await nav.share(titulo === undefined ? { text: texto } : { text: texto, title: titulo });
      return 'compartido';
    } catch (error) {
      // Cerrar la hoja de compartir es una decisión del usuario, no un fallo: caer al
      // portapapeles aquí sería copiar algo que acaba de decir que no quería mandar.
      if (esCancelacion(error)) return 'cancelado';
      // Cualquier otro fallo (destino que revienta, permiso denegado) sí merece el
      // camino alternativo: el usuario quería compartir y hay que dejarle hacerlo.
    }
  }

  return copiar(texto, nav);
}

async function copiar(texto: string, nav: NavegadorCompartir): Promise<ResultadoCompartir> {
  const escribir = nav.clipboard?.writeText;
  if (typeof escribir !== 'function') return 'sin-soporte';
  try {
    await escribir.call(nav.clipboard, texto);
    return 'copiado';
  } catch {
    // El portapapeles puede estar denegado por permisos. El texto sigue a la vista en la
    // pantalla, así que la salida es decirle al usuario que lo seleccione él.
    return 'sin-soporte';
  }
}

function esCancelacion(error: unknown): boolean {
  return error instanceof Error && error.name === 'AbortError';
}
